import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { X, RotateCcw, Clock } from "lucide-react";

/**
 * Format a relative timestamp like "3 hours ago", "Yesterday"
 */
function formatRelativeTime(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin} min ago`;
  if (diffHr < 24) return `${diffHr} hour${diffHr > 1 ? "s" : ""} ago`;
  if (diffDay === 1) return "Yesterday";
  if (diffDay < 7) return `${diffDay} days ago`;
  return date.toLocaleDateString();
}

/**
 * Compute a line-by-line diff using LCS (longest common subsequence)
 */
function computeLineDiff(oldText, newText) {
  const oldLines = (oldText || "").split("\n");
  const newLines = (newText || "").split("\n");
  const m = oldLines.length;
  const n = newLines.length;

  // Build LCS table
  const dp = Array(m + 1)
    .fill(null)
    .map(() => Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (oldLines[i - 1] === newLines[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  // Backtrack to produce diff
  const result = [];
  let i = m,
    j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldLines[i - 1] === newLines[j - 1]) {
      result.unshift({ type: "unchanged", text: newLines[j - 1] });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      result.unshift({ type: "added", text: newLines[j - 1] });
      j--;
    } else {
      result.unshift({ type: "removed", text: oldLines[i - 1] });
      i--;
    }
  }

  return result;
}

const VersionHistoryPanel = ({
  isOpen,
  onClose,
  songTitle,
  versions = [],
  selectedVersion,
  onSelectVersion,
  selectedVersionContent,
  previousVersionContent,
  isLoading,
  onRestore,
  isMobile = false,
}) => {
  const [showConfirmRestore, setShowConfirmRestore] = useState(false);
  const [showDiff, setShowDiff] = useState(false);

  // Reset state when panel opens/closes
  useEffect(() => {
    if (!isOpen) {
      setShowConfirmRestore(false);
      setShowDiff(false);
    }
  }, [isOpen]);

  const diffLines = useMemo(() => {
    if (!showDiff || !selectedVersionContent || !previousVersionContent)
      return null;
    return computeLineDiff(previousVersionContent, selectedVersionContent);
  }, [showDiff, selectedVersionContent, previousVersionContent]);

  if (!isOpen) return null;

  const handleRestore = () => {
    if (selectedVersion) {
      onRestore(
        selectedVersion.versionNumber || selectedVersion.version_number,
      );
      setShowConfirmRestore(false);
    }
  };

  const selectedVersionNumber =
    selectedVersion?.versionNumber || selectedVersion?.version_number;

  // No previous version means this is v1 — nothing to diff against
  const hasPreviousVersion = previousVersionContent != null;

  return createPortal(
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Backdrop */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        }}
        onClick={onClose}
      />

      {/* Modal */}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          width: isMobile ? "100%" : "480px",
          maxWidth: isMobile ? "100%" : "90vw",
          height: isMobile ? "100%" : "auto",
          maxHeight: isMobile ? "100%" : "85vh",
          borderRadius: isMobile ? 0 : "12px",
          paddingTop: isMobile ? "env(safe-area-inset-top, 24px)" : undefined,
          border: isMobile
            ? "none"
            : "2px solid var(--color-gold-500, #d4a843)",
          overflow: "hidden",
        }}
        className="bg-forest-900 shadow-2xl"
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 20px",
            borderBottom: "1px solid var(--color-forest-600, #3a4a3a)",
            flexShrink: 0,
          }}
          className="bg-forest-800"
        >
          <div style={{ minWidth: 0 }}>
            <h2
              className="text-gold-500"
              style={{
                fontSize: "18px",
                fontWeight: 600,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              Version History
            </h2>
            <p
              className="text-forest-200"
              style={{
                fontSize: "14px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {songTitle}
            </p>
          </div>
          <button
            onClick={onClose}
            className="hover:bg-forest-700 text-forest-200"
            style={{ padding: "8px", borderRadius: "8px", flexShrink: 0 }}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Version List */}
        <div
          style={{
            overflowY: "auto",
            flexShrink: 0,
            maxHeight: isMobile ? "40%" : "300px",
            borderBottom: "1px solid var(--color-forest-600, #3a4a3a)",
          }}
        >
          {isLoading ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "48px 0",
              }}
            >
              <Clock
                className="w-5 h-5 text-forest-300 animate-pulse"
                style={{ marginRight: "8px" }}
              />
              <span className="text-forest-300">Loading versions...</span>
            </div>
          ) : versions.length === 0 ? (
            <div style={{ textAlign: "center", padding: "48px 16px" }}>
              <Clock
                className="w-8 h-8 text-forest-400"
                style={{ margin: "0 auto 12px" }}
              />
              <p className="text-forest-300" style={{ fontSize: "14px" }}>
                No version history yet.
              </p>
              <p
                className="text-forest-400"
                style={{ fontSize: "12px", marginTop: "4px" }}
              >
                Versions are saved automatically as you edit.
              </p>
            </div>
          ) : (
            <div
              style={{
                padding: "8px",
                display: "flex",
                flexDirection: "column",
                gap: "6px",
              }}
            >
              {versions.map((v) => {
                const vNum = v.versionNumber || v.version_number;
                const isSelected = selectedVersionNumber === vNum;
                const wordCount = v.wordCount || v.word_count || 0;
                const lineCount = v.lineCount || v.line_count || 0;
                const summary = v.changeSummary || v.change_summary || "";
                const createdAt = v.createdAt || v.created_at;

                // Calculate word delta from previous version
                const idx = versions.indexOf(v);
                const prevVersion = versions[idx + 1]; // versions sorted DESC
                const prevWords = prevVersion
                  ? prevVersion.wordCount || prevVersion.word_count || 0
                  : 0;
                const wordDelta = prevVersion
                  ? wordCount - prevWords
                  : wordCount;

                return (
                  <button
                    key={vNum}
                    onClick={() => onSelectVersion(v)}
                    className={`w-full text-left p-3 rounded-lg transition-colors ${
                      isSelected
                        ? "bg-forest-700 border border-gold-400 shadow-sm shadow-gold-500/10"
                        : "bg-forest-800 border border-forest-700 hover:border-forest-500"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-gold-500 font-medium text-sm">
                        v{vNum}
                      </span>
                      <span className="text-forest-300 text-xs">
                        {createdAt ? formatRelativeTime(createdAt) : ""}
                      </span>
                    </div>
                    {summary && (
                      <p className="text-forest-300 text-xs mb-1 truncate">
                        {summary}
                      </p>
                    )}
                    <div className="flex items-center gap-2">
                      {wordDelta !== 0 && (
                        <span
                          className={`text-xs px-1.5 py-0.5 rounded ${
                            wordDelta > 0
                              ? "bg-leaf-900 text-leaf-100"
                              : "bg-ember-900 text-ember-200"
                          }`}
                        >
                          {wordDelta > 0 ? "+" : ""}
                          {wordDelta} words
                        </span>
                      )}
                      <span className="text-forest-400 text-xs">
                        {wordCount}w / {lineCount}L
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Preview Area */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          {selectedVersion && (
            <>
              {/* Toggle bar */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 16px",
                  borderBottom: "1px solid var(--color-forest-700, #2d3d2d)",
                  flexShrink: 0,
                }}
              >
                <button
                  onClick={() => setShowDiff(false)}
                  className={`text-xs px-3 py-1 rounded transition-colors ${
                    !showDiff
                      ? "bg-gold-500 text-forest-900"
                      : "bg-forest-700 text-forest-200 hover:bg-forest-600"
                  }`}
                >
                  Full Text
                </button>
                <button
                  onClick={() => {
                    if (hasPreviousVersion) setShowDiff(true);
                  }}
                  className={`text-xs px-3 py-1 rounded transition-colors inline-flex items-center gap-1 ${
                    !hasPreviousVersion
                      ? "bg-forest-700 text-forest-400 cursor-not-allowed"
                      : showDiff
                        ? "bg-gold-500 text-forest-900"
                        : "bg-forest-700 text-forest-200 hover:bg-forest-600"
                  }`}
                  title={
                    !hasPreviousVersion
                      ? "No previous version to compare"
                      : "Show changes from previous version"
                  }
                >
                  Show Changes
                </button>
              </div>

              {/* Lyrics preview */}
              <div style={{ flex: 1, overflowY: "auto", padding: "12px 16px" }}>
                {selectedVersionContent ? (
                  showDiff && diffLines ? (
                    <pre className="whitespace-pre-wrap text-sm font-sans leading-relaxed">
                      {diffLines.map((line, i) => (
                        <div
                          key={i}
                          style={{
                            padding: "1px 6px",
                            marginLeft: "-6px",
                            marginRight: "-6px",
                            borderRadius: "3px",
                            backgroundColor:
                              line.type === "added"
                                ? "rgba(74, 222, 128, 0.15)"
                                : line.type === "removed"
                                  ? "rgba(248, 113, 113, 0.15)"
                                  : "transparent",
                          }}
                        >
                          <span
                            style={{
                              display: "inline-block",
                              width: "18px",
                              userSelect: "none",
                              opacity: 0.5,
                              fontSize: "11px",
                            }}
                            className={
                              line.type === "added"
                                ? "text-leaf-100"
                                : line.type === "removed"
                                  ? "text-ember-200"
                                  : "text-forest-400"
                            }
                          >
                            {line.type === "added"
                              ? "+"
                              : line.type === "removed"
                                ? "-"
                                : " "}
                          </span>
                          <span
                            className={
                              line.type === "added"
                                ? "text-leaf-100"
                                : line.type === "removed"
                                  ? "text-ember-200 line-through opacity-70"
                                  : "text-forest-100"
                            }
                          >
                            {line.text || "\u00A0"}
                          </span>
                        </div>
                      ))}
                    </pre>
                  ) : (
                    <pre className="whitespace-pre-wrap text-sm font-sans leading-relaxed text-forest-100">
                      {selectedVersionContent}
                    </pre>
                  )
                ) : (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                    }}
                  >
                    <span className="text-forest-300 text-sm">
                      Loading content...
                    </span>
                  </div>
                )}
              </div>

              {/* Restore button */}
              <div
                style={{
                  padding: "12px 16px",
                  borderTop: "1px solid var(--color-forest-600, #3a4a3a)",
                  flexShrink: 0,
                }}
              >
                {showConfirmRestore ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    <p className="text-forest-200" style={{ fontSize: "12px" }}>
                      This will save your current lyrics as a new version, then
                      restore to v{selectedVersionNumber}. Continue?
                    </p>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button
                        onClick={() => setShowConfirmRestore(false)}
                        className="flex-1 px-3 py-2 text-sm rounded-lg bg-forest-700 hover:bg-forest-600 text-forest-200 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleRestore}
                        className="flex-1 px-3 py-2 text-sm rounded-lg btn-gold text-forest-950 font-medium transition-colors"
                      >
                        Restore
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowConfirmRestore(true)}
                    className="w-full px-4 py-2.5 rounded-lg btn-gold text-forest-950 font-medium text-sm inline-flex items-center justify-center gap-2 transition-colors"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Restore This Version
                  </button>
                )}
              </div>
            </>
          )}

          {!selectedVersion && versions.length > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              <p className="text-forest-400 text-sm">
                Select a version to preview
              </p>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default VersionHistoryPanel;
